/**
 * These pieces of logic are almost entirely borrowed from recompose,
 * the exception being the function comparison in `is`.
 */
import { has, pick } from 'lodash';
import { shouldUpdate } from 'recompose';

function is(x, y) {
  // Many props are anonymous functions and do not require updating the components.
  // A re-render for an unused prop will create new anonymous functions, and will
  // trigger a re-render for the entire component tree even with this HOC applied.
  if (typeof x === 'function' && typeof y === 'function') {
    return true;
  }

  // SameValue algorithm
  if (x === y) {
    // Steps 1-5, 7-10
    // Steps 6.b-6.e: +0 != -0
    // Added the nonzero y check to make Flow happy, but it is redundant
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  }

  // Step 6.a: NaN == NaN
  // eslint-disable-next-line no-self-compare
  return x !== x && y !== y;
}

/**
 * Performs equality by iterating through keys on an object and returning false
 * when any key has values which are not strictly equal between the arguments.
 * Returns true when the values of all keys are strictly equal.
 */
function shallowEqual(objA, objB) {
  if (is(objA, objB)) {
    return true;
  }

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  const keysNotSimilar = keysA.reduce((acc, key, i) => {
    if (!has(objB, key) || !is(objA[key], objB[key])) {
      return acc.concat(false);
    }
    return acc;
  }, []);

  if (keysNotSimilar.length > 0) {
    return false;
  }

  return true;
}

const onlyUpdateForKeys = propKeys =>
  shouldUpdate(
    (props, nextProps) =>
      !shallowEqual(pick(props, propKeys), pick(nextProps, propKeys)),
  );

const onlyUpdateForPropTypes = WrappedComponent => {
  if (!WrappedComponent) {
    return WrappedComponent;
  }

  const { propTypes } = WrappedComponent;

  /* istanbul ignore next */
  if (process.env.NODE_ENV !== 'production') {
    if (!propTypes) {
      /* eslint-disable */
      console.error(`
        A component without any propTypes was passed to onlyUpdateForPropTypes().
        Check the implementation of the component with display name ${
          WrappedComponent.displayName
        }.
      `);
      /* eslint-enable */
    }
  }

  return onlyUpdateForKeys(Object.keys(propTypes))(WrappedComponent);
};

export default onlyUpdateForPropTypes;



// WEBPACK FOOTER //
// ./src/utils/onlyUpdateForPropTypes.js